import { useEffect, useEffectEvent } from "react";

import { FormattedMessage } from "react-intl";

import { Button } from "@/components/atoms/Button";
import { ListingData } from "~/context/ListingPageDataContext";
import { AppModalKey, useAppModal } from "~/modals";

import { useBookListingContext } from "../BookListingContext";

type PriceDetailsButtonProps = {
  listingData: ListingData;
  disabled?: boolean;
};

/**
 * Opens `ListingPriceDetailsDrawer` with the booking's quote. The drawer sits above
 * `BookListingContext`, so while it is open this re-pushes the quote on every change,
 * and removes it on unmount, as the drawer used to unmount along with this button.
 */
export const PriceDetailsButton = ({
  listingData,
  disabled = false,
}: PriceDetailsButtonProps) => {
  const { quote, estimate, isFetchingQuote } = useBookListingContext();
  const priceDetailsDrawer = useAppModal(AppModalKey.ListingPriceDetailsDrawer);

  const handleClick = () => {
    window.analytics?.track("view_pricing_FSV", {
      is_full_service_venue: !!listingData.fullService?.active,
      listing_id: listingData.listingId,
    });
    priceDetailsDrawer.openModal({
      estimate,
      isFetchingQuote,
      listingData,
      quote,
    });
  };

  // An effect event, so it reads the latest open state without the effect re-running
  // on it; the modal hook is a new object every render.
  const syncDrawer = useEffectEvent(() => {
    if (priceDetailsDrawer.isModalOpen) {
      priceDetailsDrawer.openModal({
        estimate,
        isFetchingQuote,
        listingData,
        quote,
      });
    }
  });

  useEffect(() => {
    syncDrawer();
  }, [estimate, isFetchingQuote, listingData, quote]);

  const removeDrawer = useEffectEvent(() => {
    priceDetailsDrawer.removeModal();
  });

  useEffect(() => () => removeDrawer(), []);

  return (
    <Button
      variant="outline-1"
      appearance="white"
      fullWidth={true}
      disabled={disabled}
      testId="listing-price-details-button"
      onClick={handleClick}
    >
      <FormattedMessage defaultMessage="Price details" />
    </Button>
  );
};
